import moment from "moment";
import LostDeal from "../../../../mainDeals/deals/deal/lost-deal/lost-form-deal";
import DealQuickView from "../../../../mainDeals/deals/deal/popup";
import WonDeal from "../../../../mainDeals/deals/deal/won-deal/won-form-deal";
import EmployeeDirectoryAddUpdate from "../../../../organization/emplyee-directory/add-update";
import {
  Calendar,
  Check,
  Clock,
  Copy,
  CopyCheck,
  Pencil,
  Settings2,
  ThumbsDown,
  ThumbsUp,
  Trophy,
  User,
  XCircle,
} from "lucide-react";
import InnerLoader from "../../../../../component/ui/loader/inner-loader";
import Swal from "sweetalert2";
import { useApiCall } from "../../../../../component/custom-hooks/api-call";
import { Suspense, useState } from "react";
import Style from "../style.module.css";
import CustomizeTabsOverviewPopup from "./customize-tabs-popup";
import { useMemo } from "react";
import FilesTab from "../../../../../component/files";
import CommentsSection from "../../../../../component/comment";

const CopyIcon = ({ text }) => {
  const [isCopied, setIsCopied] = useState(false);

  const copyToClipboard = async (text) => {
    try {
      await navigator.clipboard.writeText(text);
      setIsCopied(true);
      setTimeout(() => {
        setIsCopied(false);
      }, 3000);
    } catch (err) {
      console.error("Failed to copy:", err);
    }
  };

  return (
    <>
      <div
        className="ms-2 hover:opacity-50 cursor-pointer transition-all"
        onClick={() => {
          copyToClipboard(text);
        }}
      >
        {isCopied ? (
          <CopyCheck size={15} color="#333" />
        ) : (
          <Copy size={15} color="#333" />
        )}
      </div>
    </>
  );
};

function NewField({ label, value, icon: Icon }) {
  const [isActive, setIsActive] = useState(false);
  const isUrl =
    typeof value === "string" &&
    (value.startsWith("http://") || value.startsWith("https://"));
  return (
    <div>
      {isUrl ? (
        <>
          <div className="flex flex-col gap-y-[10px]">
            <div className="flex gap-[10px] items-center">
              {Icon ? (
                <span>{Icon ? <Icon size={16} /> : <User size={16} />} </span>
              ) : null}
              <p className="text-xs font-medium">{label}</p>
            </div>

            <div className="flex justify-between text-[14px] w-full font-normal text-[#999]">
              <a
                href={value}
                target="_blank"
                rel="noopener noreferrer"
                className=" text-gray-500 hover:underline "
              >
                {value?.length > 100 ? `${value?.slice(0, 50)}...` : value}
              </a>

              <CopyIcon text={value} />
            </div>
          </div>
        </>
      ) : (
        <div className="flex flex-col gap-y-[10px]">
          <div className="flex gap-[10px] items-center">
            {Icon ? (
              <span>{Icon ? <Icon size={16} /> : <User size={16} />} </span>
            ) : null}
            <p className="text-xs font-medium">{label}</p>
          </div>

          <div className="text-sm font-medium text-[rgba(14,18,27,0.40)]">
            {value}
          </div>
        </div>
      )}
    </div>
  );
}

const Details = ({
  leadDetail,
  DealStages = [],
  onSuccess = () => {},
  onError = () => {},
  activeDealData = {},
  updateData = () => {},
  clientDeals = [],
}) => {
  const [showEditModal, setShowEditModal] = useState(false);
  const [showWonModal, setShowWonModal] = useState(false);
  const [wonFormDeal, setWonFormDeal] = useState(null);
  const [showLostModal, setShowLostModal] = useState(false);
  const [lostFormDeal, setLostFormDeal] = useState(null);

  const [empPopup, setEmpPopup] = useState(false);
  const [empData, setEmpData] = useState({});

  const [localStage, setLocalStage] = useState(
    activeDealData?.current_stage?.dealstatus ?? "-"
  );

  const apiCall = useApiCall();

  const handleSuccess = (message) => {
    if (onSuccess) onSuccess(message); // ✅ call parent prop
  };

  const handleError = (message) => {
    if (onError) onError(message); // ✅ call parent prop
  };

  const metas = Object.fromEntries(
    activeDealData?.metas?.map((m) => [m.meta_key, m.meta_value]) ?? []
  );

  const isWon = activeDealData?.current_stage?.dealstatus === "Won";
  const isLost = activeDealData?.current_stage?.dealstatus === "Lost";
  const currentStageIndex = DealStages.findIndex(
    (s) => s.name === activeDealData?.current_stage?.dealstatus
  );
  const activeColor = isWon ? "#22c55e" : isLost ? "#ef4444" : "#2563eb";
  const isWonOrLost = isWon || isLost;

  const formatStageKey = (stage) => {
    return stage.toLowerCase().replace(/\s+/g, "_"); // "In Progress" -> "in_progress"
  };

  // --- HANDLE STAGE CHANGE ---
  const handleStageChange = async (newStage) => {
    const lowerStage = formatStageKey(newStage); // lowercase + underscores
    const currentStage = activeDealData?.current_stage?.dealstatus ?? "-"; // ✅ added

    if (lowerStage === "won") {
      setWonFormDeal(activeDealData);
      setShowWonModal(true);
      return;
    }

    if (lowerStage === "lost") {
      setLostFormDeal({
        dealkey: activeDealData.dealkey,
        name: activeDealData.name,
      });
      setShowLostModal(true);
      return;
    }

    const resultSwal = await Swal.fire({
      title: `Move deal "${activeDealData.name}"?`,
      text: `Move from "${currentStage}" to "${newStage}"?`, // ✅ fixed reference
      icon: "question",
      showCancelButton: true,
      confirmButtonText: "Yes",
      cancelButtonText: "No",
      reverseButtons: true,
    });

    if (resultSwal.isConfirmed) {
      try {
        await apiCall({
          method: "post",
          url: "deals/update-deal-status",
          data: { dealkey: activeDealData.dealkey, dealstatus: lowerStage },
        });

        // **Update card immediately without reload**
        // onStatusChange(activeDealData.dealid, newStage); // ✅ keep `newStage` or `lowerStage` depending on your display logic

        const NewClientDeals = clientDeals.map((d) =>
          d.dealid === activeDealData.dealid
            ? {
                ...d,
                current_stage: {
                  ...d.current_stage,
                  dealstatus: newStage,
                },
              } // new object
            : d
        );

        updateData(
          {
            ...activeDealData,
            current_stage: {
              ...activeDealData.current_stage,
              dealstatus: newStage,
            },
          },
          NewClientDeals
        );

        Swal.fire("Success!", `Deal moved to "${newStage}"`, "success");
      } catch (err) {
        Swal.fire(
          "Error",
          err?.response?.data?.message || "Something went wrong",
          "error"
        );
      }
    }
  };

  const campaignKeys = [
    "parent_url",
    "user_agent",
    "campaigned",
    "is_dropoff",
    "adgroupid",
    "keyword",
    "device",
    "matchtype",
    "msclkid",
    "utm_source",
    "utm_medium",
    "utm_campaign",
    "utm_term",
    "utm_content",
    "gclid",
  ];

  const excludeKeys = [
    "name",
    "email",
    "phone",
    "lead_source",
    "leadstatus",
    "leadtime",
    "country",
    "date",
  ];

  const campaignMeta = activeDealData?.metas?.filter((m) =>
    campaignKeys.includes(m.meta_key)
  );

  const otherMeta = activeDealData?.metas?.filter(
    (m) =>
      !campaignKeys.includes(m.meta_key) && !excludeKeys.includes(m.meta_key)
  );
  return (
    <div className="flex flex-col gap-[10px]  w-full h-full overflow-auto relative">
      {/* PROGRESS / STAGES */}
      <div className="flex w-full overflow-hidden pl-[2%] sticky top-0 bg-white z-10 pb-2 flex-shrink-0 pt-2">
        {DealStages?.map((stage, index) => (
          <div
            key={index}
            onClick={() => !isWonOrLost && handleStageChange(stage.name)} // s.name will automatically go lowercase in handleStageChange
            className={`
        relative px-10 py-4 text-sm font-medium flex-1 
        flex items-center justify-center -mx-[2%]
        ${
          index <= currentStageIndex && !isWonOrLost
            ? "bg-[#0057FB] text-white"
            : "bg-[#E5ECF2] text-[#666]"
        }
        `}
            style={{
              clipPath:
                index === 0
                  ? "polygon(80% 0px, 90% 50%, 80% 100%, 0% 100%, 10% 50%, 0% 0%)"
                  : "polygon(80% 0px, 90% 50%, 80% 100%, 0% 100%, 10% 50%, 0% 0%)",
              cursor: isWonOrLost ? "not-allowed" : "pointer",
            }}
          >
            {stage.name}
          </div>
        ))}
      </div>

      {/* DETAILS */}
      <div className="bg-white rounded-[10px] border border-[#e1e4ea] p-5 flex flex-col gap-[10px]">
        <div className="flex items-center justify-between">
          <h2 className="text-[#333333] text-[20px]">Details</h2>

          {!isWonOrLost ? (
            <button
              onClick={() => !isWonOrLost && setShowEditModal(true)}
              className={`flex items-center gap-[10px] px-[9px] py-[5px]  transition ${
                isWonOrLost ? "opacity-40 cursor-not-allowed" : ""
              }`}
              title="Edit Deal"
              disabled={isWonOrLost}
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="24"
                height="24"
                viewBox="0 0 24 24"
                fill="none"
              >
                <path
                  d="M11 4.00023H6.8C5.11984 4.00023 4.27976 4.00023 3.63803 4.32721C3.07354 4.61483 2.6146 5.07378 2.32698 5.63826C2 6.28 2 7.12008 2 8.80023V17.2002C2 18.8803 2 19.7204 2.32698 20.3622C2.6146 20.9266 3.07354 21.3856 3.63803 21.6732C4.27976 22.0002 5.11984 22.0002 6.8 22.0002H15.2C16.8802 22.0002 17.7202 22.0002 18.362 21.6732C18.9265 21.3856 19.3854 20.9266 19.673 20.3622C20 19.7204 20 18.8803 20 17.2002V13.0002M7.99997 16.0002H9.67452C10.1637 16.0002 10.4083 16.0002 10.6385 15.9449C10.8425 15.8959 11.0376 15.8151 11.2166 15.7055C11.4184 15.5818 11.5914 15.4088 11.9373 15.0629L21.5 5.50023C22.3284 4.67181 22.3284 3.32866 21.5 2.50023C20.6716 1.67181 19.3284 1.6718 18.5 2.50023L8.93723 12.0629C8.59133 12.4088 8.41838 12.5818 8.29469 12.7836C8.18504 12.9626 8.10423 13.1576 8.05523 13.3617C7.99997 13.5919 7.99997 13.8365 7.99997 14.3257V16.0002Z"
                  stroke="black"
                  stroke-width="2"
                  stroke-linecap="round"
                  stroke-linejoin="round"
                />
              </svg>
              <span className="text-[#999999] text-[14px]">Edit Deal</span>
            </button>
          ) : null}
        </div>

        <div>
          <div className="grid grid-cols-2 gap-4">
            <div className="py-[17px] flex flex-col gap-y-[34px] col-span-1">
              <NewField label="Name" value={activeDealData?.name} />

              <NewField
                label="Current Status"
                value={
                  isWon || isLost ? (
                    <span
                      className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-medium ${
                        isWon
                          ? "bg-green-100 text-green-700"
                          : "bg-rose-100 text-rose-700"
                      }`}
                    >
                      {isWon ? (
                        <Trophy className="size-3" />
                      ) : (
                        <XCircle className="size-3" />
                      )}
                      {isWon ? "Won" : "Lost"}
                    </span>
                  ) : (
                    <span className="inline-block bg-blue-500 text-white px-3 py-1 rounded-full text-sm">
                      {activeDealData?.current_stage?.dealstatus}
                    </span>
                  )
                }
              />

              <NewField
                label="Deal Product"
                value={activeDealData?.deal_product_name}
              />
            </div>

            <div className="py-[17px] flex flex-col gap-y-[34px] col-span-1">
              <NewField
                label="Package Name"
                value={activeDealData?.deal_package_name}
              />
              <NewField label="Amount" value={activeDealData?.dealamount} />

              <NewField label="Deal Key" value={activeDealData?.dealkey} />
            </div>
          </div>

          <div className="flex items-center justify-between">
            <NewField label="Date" value={activeDealData?.created_at} />

            <div className="flex gap-2 items-center">
              <div className="flex gap-1 items-center">
                <button
                  onClick={() => !isWonOrLost && handleStageChange("Won")}
                  title="Mark as Won"
                  disabled={isWonOrLost} // 👈 disables if won OR lost
                  className={`p-2 rounded-full transition ${
                    isWon
                      ? "bg-green-500 text-white cursor-not-allowed"
                      : isWonOrLost
                      ? "bg-slate-100 text-slate-400 cursor-not-allowed"
                      : "bg-slate-100 text-slate-500 hover:bg-green-100 hover:text-green-600"
                  }`}
                >
                  <ThumbsUp className="size-4" />
                </button>

                <button
                  onClick={() => !isWonOrLost && handleStageChange("Lost")}
                  title="Mark as Lost"
                  disabled={isWonOrLost} // 👈 disables if won OR lost
                  className={`p-2 rounded-full transition ${
                    isLost
                      ? "bg-rose-500 text-white cursor-not-allowed"
                      : isWonOrLost
                      ? "bg-slate-100 text-slate-400 cursor-not-allowed"
                      : "bg-slate-100 text-slate-500 hover:bg-rose-100 hover:text-rose-600"
                  }`}
                >
                  <ThumbsDown className="size-4" />
                </button>
              </div>

              <button
                onClick={() => {
                  setEmpPopup(true);
                  setEmpData(activeDealData);
                }}
                className="rounded-full border-2 border-black px-3 py-2 text-sm font-medium hover:bg-slate-100 transition-all"
              >
                Complete Details
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Deal Details */}
      <div className="bg-white rounded-[10px] border border-[#e1e4ea] p-5 flex flex-col gap-[10px]">
        <div className="flex items-center justify-between">
          <h2 className="text-[#333333] text-[20px]">Deal Details</h2>
        </div>

        <div className="grid  md:grid-cols-2 py-[17px]  gap-y-[34px]">
          {activeDealData?.stats?.length > 0 ? (
            activeDealData.stats.map((item, i) => (
              <NewField
                key={i}
                label={item.key.replaceAll("_", " ")} // Convert deal_amount → Deal Amount
                value={item.value || "-"}
              />
            ))
          ) : (
            <p className="text-gray-500 text-sm col-span-2">
              No deal details available.
            </p>
          )}
        </div>
      </div>

      {/* Made By */}
      <div className="bg-white rounded-[10px] border border-[#e1e4ea] p-5 flex flex-col gap-[10px]">
        <div className="flex items-center justify-between">
          <h2 className="text-[#333333] text-[20px]">Made By</h2>
        </div>

        <div className="grid grid-cols-2 gap-4">
          <div className="py-[17px] flex flex-col gap-y-[34px] col-span-1">
            <NewField label="Name" value={activeDealData?.made_by?.name} />
            <NewField label="Phone" value={activeDealData?.made_by?.phone} />
          </div>

          <div className="py-[17px] flex flex-col gap-y-[34px] col-span-1">
            <NewField
              label="Source"
              value={activeDealData?.made_by?.page_source}
            />

            <NewField label="Email" value={activeDealData?.made_by?.email} />
          </div>
        </div>
      </div>

      {/* Other Information */}
      <div className="bg-white   p-5 rounded-[10px] border border-[#E1E4EA]">
        {/* Other Information*/}
        <div className="flex flex-col gap-y-[10px]">
          {/* Header Section */}
          <div className="flex justify-between items-center">
            <p className="text-xl font-medium leading-normal">
              Additonal Information
            </p>
          </div>

          <div>
            {campaignMeta?.length > 0 && (
              <div className="grid gap-[15px] grid-cols-3">
                {campaignMeta.map((st, i) => (
                  <NewField
                    key={i}
                    label={st.meta_key
                      .replaceAll("_", " ")
                      .replace(/\b\w/g, (char) => char.toUpperCase())}
                    value={st.meta_value || "-"}
                  />
                ))}
              </div>
            )}
          </div>

          <div>
            {otherMeta?.length > 0 && (
              <div className="grid gap-[15px] grid-cols-3">
                {otherMeta.map((st, i) => (
                  <NewField
                    key={i}
                    label={st.meta_key
                      .replaceAll("_", " ")
                      .replace(/\b\w/g, (char) => char.toUpperCase())}
                    value={st.meta_value || "-"}
                  />
                ))}
              </div>
            )}
          </div>
        </div>
      </div>

      {showEditModal && (
        <EmployeeDirectoryAddUpdate
          open={showEditModal}
          onClose={() => {
            setShowEditModal(false);
          }}
          data={activeDealData}
          tabIndex={1}
          contactId={leadDetail?.id}
          onSubmit={handleSuccess}
          onError={handleError}
        />
      )}

      {/* Won Deal Modal */}
      {showWonModal && (
        <WonDeal
          wonFormDeal={wonFormDeal}
          onClose={() => setShowWonModal(false)}
          onSubmit={handleSuccess}
          onError={handleError}
        />
      )}

      {showLostModal && (
        <LostDeal
          lostFormDeal={lostFormDeal}
          onClose={() => setShowLostModal(false)}
          onSubmit={handleSuccess}
          onError={handleError}
        />
      )}

      {empPopup && (
        <DealQuickView
          onClose={() => {
            setEmpPopup(false);
            setEmpData({});
          }}
          data={empData}
          DealStages={DealStages}
        />
      )}
    </div>
  );
};

const Stages = ({
  leadDetail,
  DealStages = [],
  onSuccess = () => {},
  onError = () => {},
  loading = () => {},
  activeDealData = {},
  updateData = () => {},
}) => {
  const apiCall = useApiCall();

  const isWon = activeDealData?.current_stage?.dealstatus === "Won";
  const isLost = activeDealData?.current_stage?.dealstatus === "Lost";

  return (
    <>
      {/* Deal Timeline */}

      <div className="flex flex-col gap-[10px]  w-full h-full overflow-auto relative">
        <div className="bg-white rounded-[10px] border border-[#e1e4ea] p-5 flex flex-col gap-[10px]">
          <div className="flex items-center justify-between">
            <h2 className="text-[#333333] text-[20px]">Deal Timeline</h2>
          </div>
          <div className="relative  border-slate-200 pl-4 ml-1 space-y-5">
            {activeDealData?.stages?.map((stage, index) => {
              const isLast = index === activeDealData.stages.length - 1;

              // Color map
              const statusColors = {
                "Need Analysis": "bg-yellow-500",
                "Value Proposition": "bg-blue-500",
                "Identify Decision": "bg-violet-500",
                Proposal: "bg-teal-500",
                Review: "bg-indigo-500",
                Won: "bg-green-500",
                Lost: "bg-rose-500",
              };

              const colorClass =
                statusColors[stage?.dealstatus] || "bg-gray-400";

              return (
                <div
                  key={stage?.dealstatuslogid || index}
                  className="relative pl-6"
                >
                  {/* Dot */}
                  <span
                    className={`absolute left-[-11px] top-1 w-3 h-3 rounded-full border-2 border-white shadow ${colorClass}`}
                  ></span>

                  {/* Content */}
                  <div className="flex flex-col">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <span
                          className={`text-sm font-semibold ${colorClass.replace(
                            "bg-",
                            "text-"
                          )}`}
                        >
                          {stage?.dealstatus}
                        </span>
                        <span className="text-xs text-slate-400">
                          (by {stage?.user?.name || "System"})
                        </span>
                      </div>
                      <span className="text-xs text-slate-400">
                        {stage?.logtime
                          ? moment(stage?.logtime).fromNow()
                          : "-"}
                      </span>
                    </div>

                    <div className="text-xs text-slate-500 mt-1">
                      Updated at:{" "}
                      {stage?.logtime
                        ? moment(stage?.logtime).format("DD-MMM-YYYY, hh:mm A")
                        : "-"}
                    </div>
                  </div>

                  {/* Connector Line */}
                  {!isLast && (
                    <span className="absolute left-[-3px] top-5 w-[2px] h-full bg-slate-200"></span>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </>
  );
};

const FollowUpsTab = ({
  leadDetail,
  onSuccess = () => {},
  onError = () => {},
  activeDealData = {},
}) => {
  const [showActivityModal, setShowActivityModal] = useState(false);
  const [selectedActivity, setSelectedActivity] = useState(null);
  return (
    <>
      <div className="flex flex-col gap-[10px]  w-full h-full overflow-auto">
        <div className="bg-white rounded-[10px] p-5  border border-[#e1e4ea]  flex flex-col gap-[10px]">
          {/* ✅ Activity Cards */}
          {activeDealData?.schedules?.length ? (
            <div className="space-y-3">
              {activeDealData.schedules.map((sch, i) => {
                const isCompleted = sch.status?.toLowerCase() === "completed";
                const isPending = sch.status?.toLowerCase() === "pending";
                const isHigh = sch.priority?.toLowerCase() === "high";

                const typeColors = {
                  meeting: "bg-blue-100 text-blue-700",
                  call: "bg-emerald-100 text-emerald-700",
                  email: "bg-indigo-100 text-indigo-700",
                  task: "bg-amber-100 text-amber-700",
                };
                const typeBadge =
                  typeColors[sch.type?.toLowerCase()] ||
                  "bg-gray-100 text-gray-600";

                return (
                  <div
                    key={i}
                    className="relative group rounded-xl border border-slate-200 bg-white/90 backdrop-blur-sm p-4 shadow-[0_1px_3px_rgba(0,0,0,0.06)] hover:shadow-lg transition-all duration-200"
                  >
                    {/* ✏️ Edit button — appears on hover only */}
                    <button
                      onClick={() => {
                        setSelectedActivity(sch);
                        setShowActivityModal(true);
                      }}
                      className="absolute top-3 right-3 opacity-0 group-hover:opacity-100 bg-white/70 hover:bg-white text-slate-600 hover:text-slate-800 p-1 rounded-full shadow-sm transition-all"
                    >
                      <Pencil className="h-4 w-4" />
                    </button>

                    {/* Header */}
                    <div className="flex justify-between items-start">
                      <div className="space-y-1">
                        <h3 className="text-sm font-semibold text-slate-800">
                          {sch.subject}
                        </h3>

                        <div className="flex items-center gap-2 flex-wrap">
                          <span
                            className={`px-2 py-0.5 text-[11px] font-medium rounded-full capitalize ${typeBadge}`}
                          >
                            {sch.type}
                          </span>
                          <span className="text-[11px] text-gray-500">
                            Priority:{" "}
                            <span
                              className={`font-semibold ${
                                isHigh ? "text-red-500" : "text-emerald-600"
                              }`}
                            >
                              {sch.priority}
                            </span>
                          </span>
                        </div>
                      </div>

                      <span
                        className={`px-2 py-0.5 text-[11px] font-medium rounded-full capitalize mt-1 ml-2 ${
                          isCompleted
                            ? "bg-emerald-100 text-emerald-700"
                            : isPending
                            ? "bg-amber-100 text-amber-700"
                            : "bg-gray-100 text-gray-600"
                        }`}
                      >
                        {sch.status}
                      </span>
                    </div>

                    {/* Note Section */}
                    {sch.note && (
                      <p className="mt-2 text-sm text-slate-700 border-l-2 border-emerald-400 pl-2 leading-relaxed">
                        {sch.note}
                      </p>
                    )}

                    {/* Footer */}
                    <div className="mt-3 flex flex-wrap justify-between items-center text-[12px] text-gray-500 gap-2 border-t border-slate-100 pt-2">
                      <div className="flex items-center gap-1.5">
                        <Calendar className="h-3.5 w-3.5 text-slate-400" />
                        <span>{sch.due_date}</span>
                        <Clock className="h-3.5 w-3.5 text-slate-400 ml-2" />
                        <span>{sch.due_time}</span>
                      </div>

                      <div className="flex items-center gap-1.5">
                        <User className="h-3.5 w-3.5 text-slate-400" />
                        <span className="truncate">
                          Created by <b>{sch.user?.name || "—"}</b>
                        </span>
                      </div>

                      <div className="flex items-center gap-1.5">
                        <User className="h-3.5 w-3.5 text-slate-400" />
                        <span>
                          Assigned to <b>{sch.assignee?.name || "—"}</b>
                        </span>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          ) : (
            <p className="text-gray-400 text-sm italic">
              No scheduled activities.
            </p>
          )}

          {/* ✅ Modal Integration */}
          {showActivityModal && (
            <EmployeeDirectoryAddUpdate
              onClose={() => {
                setShowActivityModal(false);
                setSelectedActivity(null);
              }}
              data={selectedActivity}
              tabIndex={2}
              onError={onError}
              onSubmit={onSuccess}
              id={leadDetail?.dealid}
              entitytype="deal"
              entityname={leadDetail.name}
            />
          )}
        </div>
      </div>
    </>
  );
};
const Timeline = ({
  DealStages = [],
  onSuccess = () => {},
  onError = () => {},
  activeDealData = {},
}) => {
  const isWon = activeDealData?.current_stage?.dealstatus === "Won";
  const isLost = activeDealData?.current_stage?.dealstatus === "Lost";

  return (
    <>
      <div className="flex flex-col gap-[10px]  w-full h-full overflow-auto relative">
        {/* Deal Activity */}

        <div className="bg-white rounded-[10px] border border-[#e1e4ea] p-5 flex flex-col gap-[10px]">
          <div className="flex items-center justify-between">
            <h2 className="text-[#333333] text-[20px]">Deal Activity</h2>
          </div>

          <div className="flex flex-col h-full mt-5">
            <div className="flex-1 overflow-y-auto pr-4">
              <div className="relative ml-1">
                {/* ✅ Single clean vertical line */}
                <div className="absolute left-3 top-0 h-full border-l-2 border-gray-200"></div>

                {activeDealData?.activities?.length ? (
                  activeDealData.activities.map((act, i) => {
                    const userName = act?.user?.name || "U";
                    const userInitial = userName.charAt(0).toUpperCase();
                    const colors = [
                      "bg-emerald-500",
                      "bg-blue-500",
                      "bg-indigo-500",
                      "bg-rose-500",
                    ];
                    const userColor = "bg-slate-200";

                    return (
                      <div key={i} className="relative pl-10 pb-6">
                        {/* ✅ User Initial Circle */}
                        <div
                          className={`absolute left-0 top-0 flex items-center justify-center w-7 h-7 rounded-full bg-slate-200 text-slate-700 text-xs font-semibold`}
                        >
                          {userInitial}
                        </div>

                        {/* ✅ Activity content properly aligned */}
                        <div className="flex flex-col gap-1 pt-0.5">
                          <div className="flex justify-between items-center">
                            <p className="text-sm font-medium text-gray-800">
                              {act.log}
                            </p>
                            <p className="text-xs text-gray-400">
                              {act.logtime}
                            </p>
                          </div>

                          {act?.user?.name && (
                            <div className="flex items-center gap-1 text-xs text-gray-500">
                              <User className="h-3 w-3 text-gray-400" />
                              <span className="leading-none">
                                by {act.user.name}
                              </span>
                            </div>
                          )}
                        </div>
                      </div>
                    );
                  })
                ) : (
                  <p className="text-gray-400">No timeline available.</p>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};
const Comments = ({ activeDealData }) => {
  return (
    <>
      <div className="flex flex-col gap-[10px]  w-full h-full overflow-auto relative">
        <div className="space-y-4 h-full">
          <CommentsSection
            type="deal"
            id={activeDealData.dealid}
            apiBaseUrl="deal"
            title="true"
          />
        </div>
      </div>
    </>
  );
};
const Files = ({ activeDealData }) => {
  return (
    <>
      <div className="flex flex-col gap-[10px]  w-full h-full overflow-auto">
        <FilesTab
          leadId={activeDealData?.dealid}
          attachments={activeDealData?.attachments}
          attachmenttype="deal"
        />
      </div>
    </>
  );
};

const Brief = ({ activeDealData }) => {
  return (
    <>
      <div className="flex flex-col gap-[10px]  w-full h-full overflow-auto ">
        <div className="bg-white rounded-[10px] p-5  border border-[#e1e4ea]  flex flex-col gap-[10px]">
          <div className="flex items-center justify-between">
            <h2 className="text-[#333333] text-[20px]">Project Brief</h2>
          </div>
          {Array.isArray(activeDealData?.briefs) &&
          activeDealData.briefs.length > 0 ? (
            <div className="divide-y divide-slate-200">
              {activeDealData.briefs.map((item, index) => {
                // 🧠 Replace all URLs in answer with clickable links showing clean names
                const formattedAnswer = item.answer
                  ? item.answer.replace(/(https?:\/\/[^\s,]+)/g, (url) => {
                      try {
                        const parts = url.split("/");
                        const fileName = parts[parts.length - 1];

                        // 🧩 Check if it looks like a file link
                        const isFile = /\.[a-zA-Z0-9]{2,5}($|\?)/.test(
                          fileName
                        );

                        let displayText;
                        if (isFile) {
                          displayText = decodeURIComponent(fileName);
                        } else {
                          const domain = new URL(url).hostname.replace(
                            "www.",
                            ""
                          );
                          const path = new URL(url).pathname.replace(
                            /^\/|\/$/g,
                            ""
                          );
                          displayText = `${domain}/${path}`.slice(0, 60); // Trim long paths
                        }

                        return `<a href="${url}" target="_blank" rel="noopener noreferrer" class="text-blue-600 underline break-all">${displayText}</a>`;
                      } catch {
                        return url;
                      }
                    })
                  : "—";

                // Add space between multiple links for readability
                const spacedAnswer = formattedAnswer.replace(
                  /<\/a>\s*(?=<a )/g,
                  "</a> &nbsp;&nbsp;"
                );

                return (
                  <div key={index} className="py-4">
                    <div className="flex flex-col sm:flex-row sm:items-start sm:gap-4">
                      <div className="sm:w-1/3">
                        <h3 className="text-sm font-semibold text-slate-700">
                          {item.question}
                        </h3>
                      </div>
                      <div className="sm:flex-1">
                        <p
                          className="text-sm text-slate-600 leading-relaxed whitespace-pre-line break-words"
                          dangerouslySetInnerHTML={{ __html: spacedAnswer }}
                        ></p>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          ) : (
            <p className="text-gray-400 italic text-sm">
              No brief data available.
            </p>
          )}
        </div>
      </div>
    </>
  );
};

const DealInfo = ({
  leadDetail,
  PackageList = [],
  UserList = [],
  ProductList = [],
  DealStages = [],
  ClientList = [],
  onSuccess = () => {},
  onError = () => {},
  loading = () => {},
  activeDealData = {},
  updateData = () => {},
  clientDeals = [],
}) => {
  const [showEditModal, setShowEditModal] = useState(false);
  const [showWonModal, setShowWonModal] = useState(false);
  const [wonFormDeal, setWonFormDeal] = useState(null);
  const [showLostModal, setShowLostModal] = useState(false);
  const [lostFormDeal, setLostFormDeal] = useState(null);

  const [empPopup, setEmpPopup] = useState(false);
  const [empData, setEmpData] = useState({});

  const [localStage, setLocalStage] = useState(
    activeDealData?.current_stage?.dealstatus ?? "-"
  );

  const apiCall = useApiCall();

  const handleSuccess = (message) => {
    if (onSuccess) onSuccess(message); // ✅ call parent prop
  };

  const handleError = (message) => {
    if (onError) onError(message); // ✅ call parent prop
  };

  // const stage = deal?.current_stage?.dealstatus ?? "-";

  const metas = Object.fromEntries(
    activeDealData?.metas?.map((m) => [m.meta_key, m.meta_value]) ?? []
  );

  const isWon = activeDealData?.current_stage?.dealstatus === "Won";
  const isLost = activeDealData?.current_stage?.dealstatus === "Lost";

  const formatStageKey = (stage) => {
    return stage.toLowerCase().replace(/\s+/g, "_"); // "In Progress" -> "in_progress"
  };

  // --- HANDLE STAGE CHANGE ---
  const handleStageChange = async (newStage) => {
    const lowerStage = formatStageKey(newStage); // lowercase + underscores
    const currentStage = activeDealData?.current_stage?.dealstatus ?? "-"; // ✅ added

    if (lowerStage === "won") {
      setWonFormDeal(activeDealData);
      setShowWonModal(true);
      return;
    }

    if (lowerStage === "lost") {
      setLostFormDeal({
        dealkey: activeDealData.dealkey,
        name: activeDealData.name,
      });
      setShowLostModal(true);
      return;
    }

    const resultSwal = await Swal.fire({
      title: `Move deal "${activeDealData.name}"?`,
      text: `Move from "${currentStage}" to "${newStage}"?`, // ✅ fixed reference
      icon: "question",
      showCancelButton: true,
      confirmButtonText: "Yes",
      cancelButtonText: "No",
      reverseButtons: true,
    });

    if (resultSwal.isConfirmed) {
      try {
        await apiCall({
          method: "post",
          url: "deals/update-deal-status",
          data: { dealkey: activeDealData.dealkey, dealstatus: lowerStage },
        });

        // **Update card immediately without reload**
        // onStatusChange(activeDealData.dealid, newStage); // ✅ keep `newStage` or `lowerStage` depending on your display logic

        updateData({
          ...activeDealData,
          current_stage: {
            ...activeDealData.current_stage,
            dealstatus: newStage,
          },
        });

        Swal.fire("Success!", `Deal moved to "${newStage}"`, "success");
      } catch (err) {
        Swal.fire(
          "Error",
          err?.response?.data?.message || "Something went wrong",
          "error"
        );
      }
    }
  };

  const TAB_CONFIG = [
    {
      key: "deals",
      label: "Deals",
      component: (
        <Details
          ProductList={ProductList}
          PackageList={PackageList}
          DealStages={DealStages}
          ClientList={ClientList}
          UserList={UserList}
          leadDetail={leadDetail}
          activeDealData={activeDealData}
          loading={loading}
          onSuccess={onSuccess}
          onError={onError}
          updateData={updateData}
          clientDeals={clientDeals}
        />
      ),
    },
    {
      key: "stages",
      label: "Stages",
      component: (
        <Stages
          ProductList={ProductList}
          PackageList={PackageList}
          DealStages={DealStages}
          ClientList={ClientList}
          UserList={UserList}
          leadDetail={leadDetail}
          activeDealData={activeDealData}
          loading={loading}
          onSuccess={onSuccess}
          onError={onError}
          updateData={updateData}
        />
      ),
    },
    {
      key: "followups",
      label: "Follow ups",
      component: (
        <FollowUpsTab
          leadDetail={leadDetail}
          activeDealData={activeDealData}
          onSubmit={onSuccess}
          onError={onError}
        />
      ),
    },
    {
      key: "timeline",
      label: "Timeline",
      component: (
        <Timeline
          ProductList={ProductList}
          PackageList={PackageList}
          DealStages={DealStages}
          ClientList={ClientList}
          UserList={UserList}
          leadDetail={leadDetail}
          activeDealData={activeDealData}
          loading={loading}
          onSuccess={onSuccess}
          onError={onError}
          updateData={updateData}
        />
      ),
    },
    {
      key: "comments",
      label: "Comments",
      component: (
        <Comments
          leadDetail={leadDetail}
          activeDealData={activeDealData}
          onSubmit={onSuccess}
          onError={onError}
        />
      ),
    },
    {
      key: "files",
      label: "Files",
      component: (
        <Files
          leadId={activeDealData?.userid}
          attachments={activeDealData?.attachments}
          attachmenttype="contact"
          activeDealData={activeDealData}
          onSubmit={onSuccess}
          onError={onError}
        />
      ),
    },
    {
      key: "briefs",
      label: "Briefs",
      component: (
        <Brief
          leadId={activeDealData?.userid}
          attachments={activeDealData?.attachments}
          attachmenttype="contact"
          activeDealData={activeDealData}
          onSubmit={onSuccess}
          onError={onError}
        />
      ),
    },
  ].filter(Boolean);

  const [tab, setTab] = useState(0);
  const [show, setShow] = useState(false);

  const [tabsCustomizeTabs, setTabsCustomizeTabs] = useState({
    deals: true,
    stages: true,
    followups: true,
    timeline: true,
    comments: true,
    files: true,
    briefs: true,
  });

  const handleTabChange = (index) => {
    setTab(index);
  };

  const renderedTabs = useMemo(() => {
    return TAB_CONFIG.map((tabItem, index) => {
      if (!tabsCustomizeTabs[tabItem.key]) return null;
      return (
        <li
          key={tabItem.key}
          className={`${tab === index ? Style.active : null}`}
          onClick={() => {
            if (tabItem?.component) {
              handleTabChange(index);
            }
          }}
        >
          <div className={Style.icon}></div>
          <span>{tabItem.label}</span>
        </li>
      );
    });
  }, [tab, tabsCustomizeTabs]);

  return (
    <>
      {/* <div> */}
      {loading ? (
        <div className="w-full h-[500px] flex justify-center items-center">
          <InnerLoader />
        </div>
      ) : (
        <div className="flex flex-col gap-[10px] pt-2 w-full h-full overflow-hidden">
          {/* Project Header */}

          <div>
            <div>
              <div className={Style.modal_header}>
                <div className={Style.modal_nav}>
                  <ul>{renderedTabs}</ul>
                </div>
                <div
                  className="flex gap-[2px] justify-center cursor-pointer text-[#999]"
                  onClick={() => setShow(true)}
                >
                  <Settings2 size={20} />
                </div>
              </div>
            </div>
          </div>

          <div className="flex flex-col h-screen overflow-hidden">
            {TAB_CONFIG.map(
              (tabItem, index) =>
                //   <div>
                tab === index && (
                  <Suspense
                    key={tabItem?.key}
                    fallback={
                      <div className="tableDataLoader">
                        <div className="tableDataLoader-wrapper">
                          <div className="loader"></div>
                          <span>Data Load...</span>
                        </div>
                      </div>
                    }
                  >
                    {tabItem?.component}
                  </Suspense>
                )
              //   </div>
            )}
          </div>
        </div>
      )}

      {showEditModal && (
        <EmployeeDirectoryAddUpdate
          open={showEditModal}
          onClose={() => {
            setShowEditModal(false);
          }}
          data={activeDealData}
          tabIndex={1}
          contactId={leadDetail?.id}
          onSubmit={handleSuccess}
          onError={handleError}
        />
      )}

      {/* Won Deal Modal */}
      {showWonModal && (
        <WonDeal
          wonFormDeal={wonFormDeal}
          onClose={() => setShowWonModal(false)}
          onSubmit={handleSuccess}
          onError={handleError}
        />
      )}

      {showLostModal && (
        <LostDeal
          lostFormDeal={lostFormDeal}
          onClose={() => setShowLostModal(false)}
          onSubmit={handleSuccess}
          onError={handleError}
        />
      )}

      {empPopup && (
        <DealQuickView
          onClose={() => {
            setEmpPopup(false);
            setEmpData({});
          }}
          data={empData}
          DealStages={DealStages}
        />
      )}

      {show && (
        <CustomizeTabsOverviewPopup
          data={TAB_CONFIG.map((t) => t)}
          tabs={tabsCustomizeTabs}
          onClose={() => setShow(false)}
          onToggle={(tabName) => {
            setTabsCustomizeTabs((prev) => ({
              ...prev,
              [tabName]: !prev[tabName],
            }));
          }}
        />
      )}
    </>
  );
};

export default DealInfo;
